import { BaseWebComponent } from "../base-components/base-web-component.js";
import { templateParse, templateRenderString } from "../../logic/templateParse.js";
import type { ZodError, ZodIssue } from "zod";
import { DataCollector } from "../data-components/data-collector.js";
import { DataSlot } from "../data-components/data-slot.js";
import { toastError } from "../../toasts.js";

type ZodErrorLike = { issues: ZodIssue[] } | null;

export class ZodErrorComponent extends BaseWebComponent {
  static webComponentName: string = "zod-error";
  protected _templateId: string = "zod-error";
  static observedAttributes: string[] = ["collector-id"];
  private get dataCollector(): DataCollector | null {
    const collectorId = this.getAttribute('collector-id');
    if (collectorId) {
      return document.getElementById(collectorId) as DataCollector;
    }
    return null;
  };
  private get dataSlots(): DataSlot[] {
    if (this.dataCollector) {
      return Array.from(this.dataCollector.querySelectorAll('data-slot'));
    }
    return [];
  }
  private _error: ZodErrorLike = null;
  private get renderErrors(): boolean {
    return this.hasAttribute("render-errors") && this.getAttribute("render-errors") !== "false";
  }
  get error(): ZodError<any> | null {
    return (this._error as ZodError<any>) || null;
  }
  set error(err: ZodError<any> | null) {
    this._error = (err && Array.isArray((err as any).issues)) ? (err as ZodError<any>) : null;

    // Log degli errori Zod in formato leggibile
    if (this._error && this._error.issues.length > 0) {
      console.group('🔴 Errori di validazione Zod:');
      this._error.issues.forEach((issue, index) => {
        const fieldPath = issue.path.length > 0 ? issue.path.join('.') : 'root';
        console.error(`${index + 1}. Campo "${fieldPath}": ${issue.message}`);
        console.log(`   - Codice errore: ${issue.code}`);

        // Gestione safe dei campi specifici per tipo di errore
        const issueAny = issue as any;
        if (issueAny.received !== undefined) {
          console.log(`   - Valore ricevuto: ${typeof issueAny.received === 'object' ? JSON.stringify(issueAny.received) : issueAny.received}`);
        }
        if (issueAny.expected !== undefined) {
          console.log(`   - Valore atteso: ${issueAny.expected}`);
        }
      });
      console.groupEnd();
    }

    // Se non c'è errore, svuota
    this.handleZodErrorInDataSlots();
    if (!this._error || this._error.issues.length === 0) {
      this.innerHTML = "";
      return;
    }
    this.render();

  }
  protected handleZodErrorInDataSlots(): void {
    if(!this.dataCollector) return;
     this.dataSlots.forEach(slot => slot.errorMessage = "");
    if (!this.error || this.error.issues.length === 0) {
      // Nessun errore, rimuovi gli stati di errore dai data-slot
     
      return;
    }
    const fieldErrors: Record<string, ZodIssue[]> = {};
    this.error.issues.forEach(issue => {
      const fieldKey = [...issue.path].pop()?.toString() || '';
      const slot = this.dataSlots.find(ds => ds.getAttribute("key") === fieldKey);
      if (slot) {
        slot.errorMessage = issue.message;
      }
    });
    if(this.error.issues.length >0) {
      toastError( "Errore di validazione dei dati, controlla i campi inseriti.");
    }
  }
  protected buildRenderingData(): Record<string, any> {
    const issues: ZodIssue[] = this._error?.issues || [];
    return {
      issues,
      count: issues.length
    };
  }

  protected render(): void {
    // Non mostrare nulla se non ci sono errori
    if (!this._error || this._error.issues.length === 0 || !this.renderErrors) {
      this.innerHTML = "";
      return;
    }
    try {
      this.renderingData = this.buildRenderingData();
      // Prova con il template registrato; altrimenti usa fallback inline
      let html = templateParse(this.templateId, this.renderingData);
      this.innerHTML = html || "";
      this.afterRender();
    } catch (error) {
      console.error(`Error in render for ${this.tagName}:`, error);
      this.onRenderError();
    }
  }
  protected onAttributeChange(name: string, oldValue: string, newValue: string): void {
    if (name === "collector-id") {
      this.render();
    }
  }
}

